import React, { useState, useEffect } from 'react';
import { emailIntegrationAPI } from '../services/api';
import Icon from './Icon';

function SuperAdminEmailIntegrations({ darkMode = true }) {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [extracting, setExtracting] = useState({});
  const [reviewing, setReviewing] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchIntegrations();
  }, [statusFilter]);

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      const response = await emailIntegrationAPI.getAllIntegrations(statusFilter || undefined);
      setIntegrations(response.data.integrations || []);
    } catch (err) {
      setError('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const fetchDetail = async (id) => {
    try {
      setDetailLoading(true);
      const response = await emailIntegrationAPI.getIntegrationDetail(id);
      setSelectedIntegration(response.data);
    } catch (err) {
      setError('Failed to load integration details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleExtract = async (sampleId) => {
    try {
      setExtracting(prev => ({ ...prev, [sampleId]: true }));
      setError('');
      await emailIntegrationAPI.extractSampleReceipt(sampleId);
      setSuccess('Extraction completed');
      setTimeout(() => setSuccess(''), 3000);
      if (selectedIntegration?.integration?.id) {
        fetchDetail(selectedIntegration.integration.id);
      }
    } catch (err) {
      setError('Extraction failed');
    } finally {
      setExtracting(prev => ({ ...prev, [sampleId]: false }));
    }
  };

  const handleReview = async (sampleId, approved) => {
    try {
      setReviewing(prev => ({ ...prev, [sampleId]: true }));
      setError('');
      await emailIntegrationAPI.reviewSampleReceipt(sampleId, approved, '');
      setSuccess(approved ? 'Sample approved' : 'Sample rejected');
      setTimeout(() => setSuccess(''), 3000);
      if (selectedIntegration?.integration?.id) {
        fetchDetail(selectedIntegration.integration.id);
      }
    } catch (err) {
      setError('Review failed');
    } finally {
      setReviewing(prev => ({ ...prev, [sampleId]: false }));
    }
  };

  const handleApprove = async () => {
    if (!selectedIntegration?.integration?.id) return;
    try {
      setError('');
      await emailIntegrationAPI.approveIntegration(selectedIntegration.integration.id, '');
      setSuccess('Integration approved and activated');
      setTimeout(() => setSuccess(''), 3000);
      fetchIntegrations();
      setSelectedIntegration(null);
    } catch (err) {
      setError('Failed to approve integration');
    }
  };

  const handleReject = async () => {
    const reason = prompt('Rejection reason:');
    if (!reason) return;
    
    if (!selectedIntegration?.integration?.id) return;
    try {
      setError('');
      await emailIntegrationAPI.rejectIntegration(selectedIntegration.integration.id, reason, '');
      setSuccess('Integration rejected');
      setTimeout(() => setSuccess(''), 3000);
      fetchIntegrations();
      setSelectedIntegration(null);
    } catch (err) {
      setError('Failed to reject integration');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'rgba(107, 114, 128, 0.15)', color: '#6b7280' },
      awaiting_samples: { bg: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' },
      samples_received: { bg: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6' },
      validating: { bg: 'rgba(139, 92, 246, 0.15)', color: '#8b5cf6' },
      active: { bg: 'rgba(16, 185, 129, 0.15)', color: '#10b981' },
      paused: { bg: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' },
      failed: { bg: 'rgba(239, 68, 68, 0.15)', color: '#ef4444' },
    };
    const style = styles[status] || styles.pending;
    
    return (
      <span style={{
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '500',
        background: style.bg,
        color: style.color,
        textTransform: 'capitalize'
      }}>
        {status?.replace(/_/g, ' ')}
      </span>
    );
  };

  return (
    <div style={{ display: 'flex', gap: '24px', height: '600px' }}>
      {/* List */}
      <div style={{
        flex: selectedIntegration ? '0 0 350px' : '1',
        background: darkMode ? 'rgba(255,255,255,0.03)' : '#fff',
        borderRadius: '12px',
        border: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '16px',
          borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: darkMode ? '#fff' : '#1f2937' }}>
              Email Integrations
            </h3>
            <span style={{
              padding: '4px 8px',
              borderRadius: '8px',
              fontSize: '12px',
              background: darkMode ? 'rgba(255,255,255,0.1)' : '#f3f4f6',
              color: darkMode ? 'rgba(255,255,255,0.7)' : '#6b7280'
            }}>
              {integrations.length} total
            </span>
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: '8px',
              border: `1px solid ${darkMode ? 'rgba(255,255,255,0.15)' : '#e5e7eb'}`,
              background: darkMode ? 'rgba(0,0,0,0.3)' : '#fff',
              color: darkMode ? '#fff' : '#1f2937',
              fontSize: '13px'
            }}
          >
            <option value="">All Status</option>
            <option value="samples_received">Samples Received (Needs Review)</option>
            <option value="awaiting_samples">Awaiting Samples</option>
            <option value="active">Active</option>
            <option value="failed">Failed</option>
          </select>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <div className="spinner" style={{ margin: '0 auto' }}></div>
            </div>
          ) : integrations.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <p style={{ color: darkMode ? 'rgba(255,255,255,0.5)' : '#9ca3af' }}>
                No integrations found
              </p>
            </div>
          ) : (
            integrations.map(integration => (
              <div
                key={integration.id}
                onClick={() => fetchDetail(integration.id)}
                style={{
                  padding: '16px',
                  borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.05)' : '#f3f4f6'}`,
                  cursor: 'pointer',
                  background: selectedIntegration?.integration?.id === integration.id 
                    ? (darkMode ? 'rgba(61, 90, 32, 0.2)' : 'rgba(61, 90, 32, 0.1)') 
                    : 'transparent',
                  transition: 'background 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <h4 style={{
                    margin: 0,
                    fontSize: '14px',
                    fontWeight: '600',
                    color: darkMode ? '#fff' : '#1f2937'
                  }}>
                    {integration.company_name}
                  </h4>
                  {getStatusBadge(integration.status)}
                </div>
                <p style={{
                  margin: 0,
                  fontSize: '12px',
                  color: darkMode ? 'rgba(255,255,255,0.5)' : '#9ca3af'
                }}>
                  {integration.sender_email || 'No email set'}
                </p>
                <p style={{
                  margin: '4px 0 0',
                  fontSize: '11px',
                  color: darkMode ? 'rgba(255,255,255,0.4)' : '#9ca3af'
                }}>
                  Samples: {integration.sample_receipts_received}/{integration.sample_receipts_required}
                </p>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Detail Panel */}
      {selectedIntegration && (
        <div style={{
          flex: 1,
          background: darkMode ? 'rgba(255,255,255,0.03)' : '#fff',
          borderRadius: '12px',
          border: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {detailLoading ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <div className="spinner" style={{ margin: '0 auto' }}></div>
            </div>
          ) : (
            <>
              {/* Detail Header */}
              <div style={{
                padding: '20px',
                borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <h3 style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: '600', color: darkMode ? '#fff' : '#1f2937' }}>
                      {selectedIntegration.integration.company_name}
                    </h3>
                    <p style={{ margin: 0, fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280' }}>
                      {selectedIntegration.integration.sender_email}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedIntegration(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      color: darkMode ? 'rgba(255,255,255,0.5)' : '#9ca3af'
                    }}
                  >
                    <Icon name="close" size={20} />
                  </button>
                </div>
                {getStatusBadge(selectedIntegration.integration.status)}
              </div>

              {/* Sample Receipts */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                <h4 style={{
                  margin: '0 0 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: darkMode ? '#fff' : '#1f2937'
                }}>
                  Sample Receipts
                </h4>

                {selectedIntegration.sample_receipts?.length === 0 ? (
                  <p style={{ color: darkMode ? 'rgba(255,255,255,0.5)' : '#9ca3af' }}>
                    No samples uploaded yet
                  </p>
                ) : (
                  selectedIntegration.sample_receipts?.map(sample => (
                    <div key={sample.id} style={{
                      padding: '16px',
                      background: darkMode ? 'rgba(255,255,255,0.05)' : '#f9fafb',
                      borderRadius: '12px',
                      marginBottom: '12px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <div>
                          <p style={{
                            margin: 0,
                            fontSize: '14px',
                            fontWeight: '500',
                            color: darkMode ? '#fff' : '#1f2937'
                          }}>
                            {sample.original_filename}
                          </p>
                          <p style={{
                            margin: '4px 0 0',
                            fontSize: '12px',
                            color: darkMode ? 'rgba(255,255,255,0.5)' : '#9ca3af'
                          }}>
                            {sample.file_type?.toUpperCase()} • Uploaded {new Date(sample.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        {sample.admin_approved && (
                          <Icon name="check" color="success" darkMode={darkMode} size={20} />
                        )}
                      </div>

                      {/* Extraction Result */}
                      {sample.extraction_attempted && (
                        <div style={{
                          padding: '12px',
                          background: darkMode ? 'rgba(0,0,0,0.3)' : '#fff',
                          borderRadius: '8px',
                          marginBottom: '12px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <Icon 
                              name={sample.extraction_successful ? 'check' : 'warning'} 
                              color={sample.extraction_successful ? 'success' : 'warning'} 
                              darkMode={darkMode} 
                              size={16} 
                            />
                            <span style={{
                              fontSize: '12px',
                              fontWeight: '500',
                              color: sample.extraction_successful ? '#10b981' : '#f59e0b'
                            }}>
                              {sample.extraction_successful 
                                ? `Extracted (${Math.round((sample.extraction_confidence || 0) * 100)}% confidence)` 
                                : 'Extraction failed'}
                            </span>
                          </div>
                          
                          {sample.extraction_result && Object.keys(sample.extraction_result).length > 0 && (
                            <pre style={{
                              margin: 0,
                              fontSize: '11px',
                              color: darkMode ? 'rgba(255,255,255,0.7)' : '#4b5563',
                              background: darkMode ? 'rgba(0,0,0,0.3)' : '#f3f4f6',
                              padding: '8px',
                              borderRadius: '4px',
                              overflow: 'auto',
                              maxHeight: '200px'
                            }}>
                              {JSON.stringify(sample.extraction_result, null, 2)}
                            </pre>
                          )}
                        </div>
                      )}

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleExtract(sample.id)}
                          disabled={extracting[sample.id]}
                          style={{
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: 'none',
                            background: 'rgba(59, 130, 246, 0.15)',
                            color: '#3b82f6',
                            fontSize: '12px',
                            cursor: extracting[sample.id] ? 'wait' : 'pointer'
                          }}
                        >
                          {extracting[sample.id] ? 'Extracting...' : 'Run AI Extraction'}
                        </button>
                        
                        {!sample.admin_reviewed && (
                          <>
                            <button
                              onClick={() => handleReview(sample.id, true)}
                              disabled={reviewing[sample.id]}
                              style={{
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: 'none',
                                background: 'rgba(16, 185, 129, 0.15)',
                                color: '#10b981',
                                fontSize: '12px',
                                cursor: reviewing[sample.id] ? 'wait' : 'pointer'
                              }}
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleReview(sample.id, false)}
                              disabled={reviewing[sample.id]}
                              style={{
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: 'none',
                                background: 'rgba(239, 68, 68, 0.15)',
                                color: '#ef4444',
                                fontSize: '12px',
                                cursor: reviewing[sample.id] ? 'wait' : 'pointer'
                              }}
                            >
                              Reject
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Footer Actions */}
              {selectedIntegration.integration.status === 'samples_received' && (
                <div style={{
                  padding: '16px 20px',
                  borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    onClick={handleReject}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '8px',
                      border: `1px solid ${darkMode ? 'rgba(239, 68, 68, 0.3)' : '#fecaca'}`,
                      background: 'rgba(239, 68, 68, 0.1)',
                      color: '#ef4444',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Reject Integration
                  </button>
                  <button
                    onClick={handleApprove}
                    className="btn btn-primary"
                    style={{ padding: '10px 20px', fontSize: '13px' }}
                  >
                    Approve & Activate
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default SuperAdminEmailIntegrations;
